/** Drag-and-drop upload zone (MUI port of the P2-13 uploader). */
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { api, type UploadResult } from "../lib/api";
import { errorText } from "../lib/errors";
import { maxUploadBytes, requireCustomerSuffix } from "../lib/states";
import { uploadNameProblem } from "../lib/uploadNames";
import { SHA_SLICE_LEN } from "../lib/config";
import { useCurrentCustomer, useEditMode } from "../hooks/useCustomer";
import { FilePicker } from "./ui/FilePicker";

const MB = 1_048_576;

export function UploadZone({
  onUploaded,
  prompt,
}: {
  onUploaded: (result: UploadResult) => void;
  /** Tool-declared input-slot label (already resolved); shown above the
   *  generic drop prompt. */
  prompt?: string;
}) {
  const { t } = useTranslation();
  const current = useCurrentCustomer();
  const edit = useEditMode();
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploaded, setUploaded] = useState<UploadResult | null>(null);

  const maxBytes = maxUploadBytes(); // server-declared via /api/meta, with local fallback
  const maxMb = Math.round(maxBytes / MB);

  const send = useCallback(
    async (file: File) => {
      setErrors([]);
      if (!edit.canEdit) {
        setErrors([t("customer.readOnlyAction", { name: edit.editor ?? "" })]);
        return;
      }
      if (!/\.xlsx$|\.xlsm$/i.test(file.name)) {
        setErrors([t("upload.badExtension", { name: file.name })]);
        return;
      }
      // the file must be named for the customer the dashboard works on
      const problem = current && requireCustomerSuffix() ? uploadNameProblem(file.name, current) : null;
      if (problem && current) {
        setErrors([
          problem.kind === "format"
            ? t("upload.badFormat", {
                name: file.name,
                code: current.code,
              })
            : t("upload.customerMismatch", {
                name: file.name,
                found: problem.found,
                customer: current.name,
                code: current.code,
              }),
        ]);
        return;
      }
      if (file.size > maxBytes) {
        setErrors([
          t("upload.tooLarge", {
            name: file.name,
            size: (file.size / MB).toFixed(1),
            max: maxMb,
          }),
        ]);
        return;
      }
      setBusy(true);
      try {
        const result = await api.upload(file);
        setUploaded(result);
        onUploaded(result);
      } catch (err) {
        setErrors([errorText(err)]);
      } finally {
        setBusy(false);
      }
    },
    [onUploaded, t, maxBytes, maxMb, current, edit],
  );
  const readOnly = !edit.canEdit;

  return (
    <Box>
      <FilePicker onFile={(file) => void send(file)}>
        {(open) => (
          <Box
            role="button"
            tabIndex={0}
            aria-label={prompt || t("tools.uploadPrompt")}
            aria-disabled={readOnly}
            onClick={readOnly ? undefined : open}
            onKeyDown={(e) => {
              if (!readOnly && (e.key === "Enter" || e.key === " ")) open();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!readOnly) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void send(file);
            }}
            sx={{
              border: "2px dashed",
              borderColor: dragging ? "primary.main" : "divider",
              bgcolor: dragging ? "action.hover" : "background.paper",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: readOnly ? "not-allowed" : "pointer",
              opacity: readOnly ? 0.6 : 1,
              transition: "all 150ms",
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
          >
            <Stack spacing={1} alignItems="center">
              <CloudUploadIcon color={dragging ? "primary" : "disabled"} fontSize="large" />
              {prompt && (
                <Typography variant="subtitle2" color="text.primary">
                  {prompt}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {readOnly
                  ? t("customer.readOnlyAction", { name: edit.editor ?? "" })
                  : busy
                    ? t("tools.uploading")
                    : t("tools.uploadPrompt")}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {t("upload.constraint", { mb: maxMb })}
                {current && requireCustomerSuffix()
                  ? ` · ${t("upload.nameHint", { code: current.code })}`
                  : ""}
              </Typography>
            </Stack>
          </Box>
        )}
      </FilePicker>
      {uploaded && (
        <Typography variant="caption" className="num" sx={{ mt: 1, display: "block" }}>
          {uploaded.display_name} · {(uploaded.byte_size / 1024).toFixed(0)} KB · sha{" "}
          {uploaded.content_sha256.slice(0, SHA_SLICE_LEN)}
        </Typography>
      )}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </Alert>
      )}
    </Box>
  );
}
